import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Search, X, Sparkles, SlidersHorizontal } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { InventoryCard } from '../components/inventory/InventoryCard';
import { InventoryFilters } from '../components/inventory/InventoryFilters';
import { FinancingPromo, PROMOS } from '../components/inventory/FinancingPromo';
import { filterVehicles, sortVehicles, buildGrid, SORTS, EMPTY_FILTERS } from '../components/inventory/inventoryUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_BG = 'https://images.unsplash.com/photo-1762195340046-415140d8b1b2?w=1920&q=80';
const HERO_BG = 'https://images.pexels.com/photos/17632052/pexels-photo-17632052.jpeg?w=1920';

const useDebounce = (value, delay) => {
  const [d, setD] = useState(value);
  useEffect(() => { const h = setTimeout(() => setD(value), delay); return () => clearTimeout(h); }, [value, delay]);
  return d;
};

const Hero = ({ en, total }) => (
  <div className="relative z-10 bg-slate-900/80 overflow-hidden" data-testid="inventory-hero">
    <div className="absolute inset-0">
      <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-50" />
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/70 to-slate-900/30" />
    </div>
    <div className="relative px-4 sm:px-8 md:px-12 lg:px-16 pt-32 md:pt-40 pb-10 sm:pb-16 md:pb-24">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 w-full">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="h-1 w-12 bg-[#4ade80] rounded-full" />
            <span className="text-[#4ade80] font-semibold text-sm uppercase tracking-wider">{en ? 'Our inventory' : 'Notre inventaire'}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-[3.25rem] font-bold text-white mb-4 leading-tight">
            {en ? 'Find Your' : 'Trouvez votre'} <span className="text-[#4ade80]">{en ? 'Ideal Vehicle' : 'véhicule idéal'}</span>
          </h1>
          <p className="text-slate-300 text-base md:text-lg lg:text-xl max-w-xl mb-6 leading-relaxed">
            {en ? 'Browse our selection of pre-owned vehicles, with financing adapted to your situation.' : 'Parcourez notre sélection de véhicules d’occasion, avec un financement adapté à votre situation.'}
          </p>
          <div className="flex flex-wrap gap-3 sm:gap-4">
            <a href="#vehicle-grid" className="bg-[#1a5c3b] hover:bg-[#165432] text-white font-semibold px-5 sm:px-8 py-3 sm:py-4 rounded-full transition-colors flex items-center gap-2 text-sm sm:text-base" data-testid="inventory-browse-btn">
              {en ? 'Browse vehicles' : 'Parcourir les véhicules'}
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </a>
            <Link to="/demander-un-pret-auto" className="bg-white/10 hover:bg-white/20 text-white font-semibold px-5 sm:px-8 py-3 sm:py-4 rounded-full transition-colors border border-white/30 text-sm sm:text-base" data-testid="inventory-prequal-btn">
              {en ? 'Credit pre-qualification' : 'Pré-qualification de crédit'}
            </Link>
            <Link to="/verifier-admissibilite" className="group bg-gradient-to-r from-green-500/20 via-green-400/20 to-green-500/20 hover:from-green-500/30 hover:to-green-500/30 text-white font-semibold px-5 sm:px-8 py-3 sm:py-4 rounded-full transition-all border border-green-400/40 hover:border-green-300 flex items-center gap-2 text-sm sm:text-base shadow-lg shadow-green-500/10 hover:scale-[1.02]" data-testid="inventory-eligibility-btn">
              <Sparkles className="w-4 h-4 text-green-300 group-hover:text-white transition-colors" />
              {en ? 'My eligibility' : 'Mon admissibilité'}
              <span className="opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all">→</span>
            </Link>
          </div>
        </div>
        <div className="flex gap-10 md:gap-14">
          {[[total ? `${total}+` : '—', en ? 'Vehicles' : 'Véhicules', 'text-white'], ['20+', en ? 'Lenders' : 'Prêteurs', 'text-[#4ade80]'], ['4.9★', 'Google', 'text-white']].map(([n, l, c], i) => (
            <div key={l} className="text-center">
              <p className={`text-4xl md:text-5xl font-bold ${c}`} data-testid={`inventory-stat-${i}`}>{n}</p>
              <p className="text-slate-400 text-base">{l}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const VehicleShowcase = () => {
  const { language } = useLanguage();
  const en = language === 'en';
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [f, setF] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState('created_at-desc');
  const [drawer, setDrawer] = useState(false);
  const q = useDebounce(f.q, 300);

  useEffect(() => {
    document.title = en ? 'Our Vehicles - Inventory | Credit Auto Reset' : 'Nos véhicules - Inventaire | Credit Auto Reset';
  }, [en]);

  const load = () => {
    setLoading(true);
    setError(false);
    axios.get(`${API}/vehicles/inventory`)
      .then((res) => { setVehicles(res.data.vehicles || []); if (res.data.error) setError(true); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const shown = useMemo(() => sortVehicles(filterVehicles(vehicles, { ...f, q }), sort), [vehicles, f, q, sort]);
  const grid = useMemo(() => buildGrid(shown, PROMOS), [shown]);

  return (
    <div
      className="min-h-screen flex flex-col relative"
      style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.6)), url(${PAGE_BG})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}
      data-testid="inventory-page"
    >
      <Hero en={en} total={vehicles.length} />

      <div className="relative z-10 flex-1 px-4 sm:px-8 md:px-12 lg:px-16 pt-10 pb-28 md:pb-10 w-full">
        <div className="flex gap-10">
          <aside className="hidden lg:block w-96 shrink-0">
            <div className="sticky top-[164px]"><InventoryFilters f={f} setF={setF} vehicles={vehicles} resultCount={shown.length} en={en} /></div>
          </aside>

          <main className="flex-1 min-w-0" id="vehicle-grid">
            <div className="lg:hidden mb-4 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={f.q}
                onChange={(e) => setF({ ...f, q: e.target.value })}
                placeholder={en ? 'Honda Civic, stock #, last 6 of VIN…' : 'Honda Civic, # stock, 6 derniers du NIV…'}
                className="w-full pl-10 pr-10 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:border-[#1a5c3b] outline-none shadow-sm"
                data-testid="inventory-search-input-mobile"
              />
              {f.q && <button type="button" onClick={() => setF({ ...f, q: '' })} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"><X className="h-5 w-5" /></button>}
            </div>

            <div className="flex items-center justify-between mb-4 gap-3">
              <p className="text-white/90" data-testid="inventory-count"><span className="font-bold text-white">{shown.length}</span> {en ? 'vehicles found' : 'véhicules trouvés'}</p>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setDrawer(true)} className="lg:hidden inline-flex items-center gap-1.5 bg-white/90 text-gray-800 rounded-md px-3 py-2 text-sm font-semibold" data-testid="inventory-mobile-filters-btn">
                  <SlidersHorizontal className="w-4 h-4" />{en ? 'Filters' : 'Filtres'}
                </button>
                <span className="text-sm text-white hidden sm:inline">{en ? 'Sort by' : 'Trier par'}</span>
                <select value={sort} onChange={(e) => setSort(e.target.value)} className="w-[170px] bg-white/90 border border-white/20 text-gray-800 rounded-md px-3 py-2 text-sm outline-none" data-testid="inventory-sort-select">
                  {SORTS.map((s) => <option key={s.v} value={s.v}>{en ? s.en : s.fr}</option>)}
                </select>
              </div>
            </div>

            {error && vehicles.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl" data-testid="api-error-message">
                <p className="text-lg text-gray-700 mb-2 font-medium">{en ? 'Connection temporarily interrupted' : 'Connexion temporairement interrompue'}</p>
                <p className="text-sm text-gray-500 mb-6">{en ? 'Our vehicles will be back in a moment.' : 'Nos véhicules seront de retour dans un instant.'}</p>
                <button type="button" onClick={load} className="bg-[#1a5c3b] text-white px-6 py-2 rounded-lg hover:bg-[#165432] font-medium" data-testid="retry-button">{en ? 'Retry' : 'Réessayer'}</button>
              </div>
            ) : loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
                {[...Array(6)].map((_, i) => <div key={i} className="bg-white/90 animate-pulse rounded-xl" style={{ aspectRatio: '16/14' }} />)}
              </div>
            ) : shown.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 auto-rows-fr" data-testid="inventory-grid">
                {grid.map((item, i) => (item.type === 'promo'
                  ? <FinancingPromo key={item.key} promo={item.data} en={en} />
                  : <InventoryCard key={item.key} v={item.data} en={en} priority={i < 4} />))}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-xl" data-testid="inventory-empty">
                <p className="text-lg text-gray-500 mb-4">{en ? 'No vehicle matches your filters.' : 'Aucun véhicule ne correspond à vos filtres.'}</p>
                <button type="button" onClick={() => setF(EMPTY_FILTERS)} className="text-[#1a5c3b] font-medium hover:underline">{en ? 'Clear filters' : 'Effacer les filtres'}</button>
              </div>
            )}
            <p className="text-xs text-white/60 mt-8">{en ? '*Prices exclude taxes and applicable fees. Subject to change without notice.' : '*Les prix excluent les taxes et frais applicables. Sujets à changement sans préavis.'}</p>
          </main>
        </div>
      </div>
      {drawer && (
        <div className="fixed inset-0 z-[80] lg:hidden" data-testid="inventory-mobile-filters">
          <div className="absolute inset-0 bg-black/60" onClick={() => setDrawer(false)} />
          <div className="absolute inset-y-0 left-0 w-[90%] max-w-sm bg-slate-100 overflow-y-auto p-3 pb-24 animate-fade-up">
            <InventoryFilters f={f} setF={setF} vehicles={vehicles} resultCount={shown.length} en={en} />
          </div>
          <button type="button" onClick={() => setDrawer(false)} className="absolute bottom-4 left-4 right-4 max-w-sm h-12 rounded-xl bg-[#1a5c3b] text-white font-bold shadow-xl flex items-center justify-center gap-2" data-testid="inventory-mobile-filters-close">
            <X className="w-4 h-4" />{en ? `Show ${shown.length} vehicles` : `Voir ${shown.length} véhicules`}
          </button>
        </div>
      )}
    </div>
  );
};

export default VehicleShowcase;
